import { Button, Empty, Modal, Table, Tabs, message } from 'antd';
import styles from './index.module.scss';
import { useGlobalStore } from '@/store';
import { useEffect, useState, useMemo } from 'react';
import { createPaymentOrder, getPaymentOrderStatus, listMcoinRecordsByPage, listPaymentProducts } from '@/services/user';
import { isWechatBrowser, isMobileBrowser } from '@/utils';
import WechatBindDialog from '@/components/WechatBindDialog';

import moment from 'moment';
import SubscriptionInfo from '@/components/SubscriptionInfo';
import SubscriptionProducts from '@/components/SubscriptionProducts';

// 微信支付接口类型声明
declare global {
  interface Window {
    WeixinJSBridge?: {
      invoke: (
        method: string,
        params: any,
        callback: (res: { err_msg: string }) => void
      ) => void;
    };
  }
}

// 支付产品接口定义
interface PaymentProduct {
  publicId: string;
  title: string;
  description?: string;
  displayPrice: number;
  price: number;
  mcoinAmount: number;
  code: string;
}

const MCoinRecordTypeMap = {
  add: '收入',
  cost: '支出',
  refund: '退款',
};

// 定义表格列类型
interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  render?: (text: string) => any;
  width?: number | string;
  ellipsis?: boolean;
}

const Member: React.FC = () => {
  const user = useGlobalStore((state) => state.currentUser);
  const getUserInfo = useGlobalStore((state) => state.getUserInfo);
  const showLoginModal = useGlobalStore((state) => state.showLoginModal);

  const [ mcoinRecord, setMcoinRecord ] = useState([]);
  const [ paymentProducts, setPaymentProducts ] = useState<PaymentProduct[]>([]);
  const [ selectedProduct, setSelectedProduct ] = useState<PaymentProduct | null>(null);
  const [ showPaymentModal, setShowPaymentModal ] = useState(false);
  const [ showBindModal, setShowBindModal ] = useState(false);
  const [ qrcode, setQrcode ] = useState('');
  const [ mwebUrl, setMwebUrl ] = useState('');
  const [ isMobile, setIsMobile ] = useState(false);
  const [ needWechatAuth, setNeedWechatAuth ] = useState(false);

  // Add pagination state for M币记录
  const [ currentPage, setCurrentPage ] = useState(1);
  const [ pageSize, setPageSize ] = useState(isMobile ? 5 : 10);
  const [ totalRecords, setTotalRecords ] = useState(0);

  // 添加刷新状态以便子组件可以触发刷新
  const [refreshKey, setRefreshKey] = useState(0);

  // 检查是否需要微信授权获取OpenID
  useEffect(() => {
    // 如果在微信环境中且没有wechatOpenId
    if (isWechatBrowser() && !user?.wechatOpenId) {
      setNeedWechatAuth(true);
    } else {
      setNeedWechatAuth(false);
    }
  }, [user]);

  // 响应式设计 - 检测设备类型
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  async function loopPaymentOrderStatus(orderId: string) {
    const res = await getPaymentOrderStatus(orderId);
    if (res.data === 'SUCCESS') {
      setShowPaymentModal(false);
      await getUserInfo();
      await fetchMcoinRecord();
      message.success('支付成功');
    } else {
      setTimeout(() => {
        loopPaymentOrderStatus(orderId);
      }, 3000);
    }
  }

  async function fetchMcoinRecord() {
    const res = await listMcoinRecordsByPage(currentPage, pageSize);
    /**
     * res.data = {
      list: result?.records || [],
      total: result?.count || 0,
      hasMore: result?.records?.length === pageSize,
    }
     */
    setMcoinRecord(res.data.list);
    setTotalRecords(res.data.total);
  }

  async function fetchPaymentProducts() {
    const res = await listPaymentProducts();
    setPaymentProducts(res.data);
  }

  const handleSelectProduct = (product: PaymentProduct) => {
    setSelectedProduct(product);
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct) return;
    
    let paymentType = 'qrcode';
    
    if (isWechatBrowser()) {
      paymentType = 'jsapi';
      
      // 如果在微信环境但没有关联微信账号，则提示用户
      if (!user?.wechatOpenId) {
        message.error('请先关联微信账号');
        showWechatBindModal();
        return;
      }
    } else if (isMobileBrowser()) {
      paymentType = 'h5';
    }
    
    const res = await createPaymentOrder(selectedProduct.code, paymentType);
    if (!res || !res.data) {
      message.error('创建订单失败');
      return;
    }
    
    const data = res.data;

    if (paymentType === 'jsapi') {
      const { appId, timeStamp, nonceStr, packageValue, signType, paySign, publicId } = data;
      if (window.WeixinJSBridge) {
        window.WeixinJSBridge.invoke('getBrandWCPayRequest', {
          appId,
          timeStamp,
          nonceStr,
          package: packageValue,
          signType,
          paySign
        }, function(res) {
          if (res.err_msg === 'get_brand_wcpay_request:ok') {
            getUserInfo();
            fetchMcoinRecord();
            message.success('支付成功');
          } else {
            message.error('支付失败或取消');
          }
        });
      } else {
        message.error('请在微信客户端打开');
      }
    } else if (paymentType === 'h5') {
      const { mwebUrl, publicId } = data;
      if (mwebUrl) {
        const redirectUrl = `${window.location.origin}/user-center/member`;
        setMwebUrl(`${mwebUrl}&redirectUrl=${encodeURIComponent(redirectUrl)}`);
        window.location.href = `${mwebUrl}&redirectUrl=${encodeURIComponent(redirectUrl)}`;
        loopPaymentOrderStatus(publicId);
      }
    } else {
      const { qrcodeUrl, publicId } = data;
      if (qrcodeUrl) {
        setQrcode(qrcodeUrl);
        setShowPaymentModal(true);
        loopPaymentOrderStatus(publicId);
      }
    }
  };

  const handleDismissModal = async () => {
    setShowPaymentModal(false);
    await getUserInfo();
    await fetchMcoinRecord();
  };

  // 显示微信授权提示框
  const showWechatAuthModal = () => {
    Modal.confirm({
      title: '需要关联微信',
      content: '在微信环境内使用JSAPI支付需要先关联您的微信账号。您可以点击"去关联"按钮进行关联。',
      okText: '去关联',
      cancelText: '取消',
      onOk: () => {
        // 显示微信绑定弹窗
        showWechatBindModal();
      }
    });
  };

  // 显示微信绑定弹窗
  const showWechatBindModal = () => {
    setShowBindModal(true);
  };

  // 关闭微信绑定弹窗
  const handleCloseBindModal = () => {
    setShowBindModal(false);
  };

  // 微信绑定成功的回调
  const handleBindSuccess = async () => {
    await getUserInfo(); // 刷新用户信息
    message.success('微信绑定成功，现在可以使用微信支付了');
    setShowBindModal(false);
  };

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // 刷新所有数据的方法
  const refreshAllData = async () => {
    await getUserInfo();
    await fetchMcoinRecord();
    await fetchPaymentProducts();
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    fetchMcoinRecord();
    fetchPaymentProducts();
  }, [currentPage, pageSize]);

  // 根据设备优化表格列
  const tableColumns = useMemo(() => {
    const baseColumns: TableColumn[] = [
      {
        title: '时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (text: string) => isMobile 
          ? moment(text).format('MM-DD HH:mm') 
          : moment(text).format('YYYY-MM-DD HH:mm:ss'),
        width: isMobile ? 90 : undefined,
      },
      {
        title: '变动',
        dataIndex: 'type',
        key: 'type',
        render: (text: string) => MCoinRecordTypeMap[text] || '-',
        width: isMobile ? 50 : undefined,
      },
      {
        title: '金额',
        dataIndex: 'amount',
        key: 'amount',
        width: isMobile ? 50 : undefined,
      }
    ];
    
    // 在非移动设备上显示更多列
    if (!isMobile) {
      baseColumns.push(
        {
          title: '描述',
          dataIndex: 'description',
          key: 'description',
          ellipsis: true,
        },
        {
          title: '余额',
          dataIndex: 'balance',
          key: 'balance',
        }
      );
    } else {
      // 在移动设备上只显示余额
      baseColumns.push({
        title: '余额',
        dataIndex: 'balance',
        key: 'balance',
        width: 50,
      });
    }
    
    return baseColumns;
  }, [isMobile]);

  return <div className={styles.member}>
    {/* 如果在微信内且需要授权，显示提示信息 */}
    {needWechatAuth && (
      <div className={styles.wechatAuthTip}>
        <div className={styles.tipContent}>
          您当前在微信环境中，但尚未关联微信账号。使用微信支付请先
          <Button type="link" onClick={showWechatBindModal}>
            关联微信账号
          </Button>
        </div>
      </div>
    )}
    
    <div className={styles.top}>
      <div className={styles.avatar}>
        <img src={user?.avatar} alt="用户头像" />
      </div>
      <div className={styles.right}>
        <div className={styles.title}>{user?.displayName || user?.name}</div>
        <div className={styles.subTitle}>M 币：{user?.mcoinAccount?.balance}</div>
      </div>
    </div>
    <Tabs defaultActiveKey="1" size={isMobile ? "small" : "middle"}>
      <Tabs.TabPane tab="M 币充值" key="1">
        <div className={styles.products}>
          {
            paymentProducts?.map((product: PaymentProduct) => (
              <div 
                key={product.publicId} 
                className={selectedProduct?.publicId === product.publicId ? styles.productSelected : styles.product} 
                onClick={handleSelectProduct.bind(null, product)}
              >
                <div className={styles.title}>{product.title}</div>
                {!isMobile && product.description && <div className={styles.description}>{product.description}</div>}
                <div className={styles.priceGroup}>
                  <div className={styles.displayPrice}>{(+product.displayPrice)/100} 元</div>
                  <div className={styles.price}>{(+product.price)/100} 元</div>
                  <div className={styles.mcoin}>{product.mcoinAmount} M 币</div>
                </div>
              </div>
            ))
          }
        </div>
        <div className={styles.action}>
          <Button type="primary" disabled={!selectedProduct} onClick={handleCreateOrder}>
            立即充值
          </Button>
        </div>
      </Tabs.TabPane>
      
      <Tabs.TabPane tab="会员订阅" key="2">
        <div className={styles.subscriptionSection}>
          {/* 当前订阅信息 */}
          <SubscriptionInfo onRefresh={refreshAllData} key={`sub-info-${refreshKey}`} />
          
          {/* 订阅产品列表 */}
          <h3 className={styles.sectionTitle}>订阅套餐</h3>
          <SubscriptionProducts onSuccess={refreshAllData} key={`sub-products-${refreshKey}`} />
        </div>
      </Tabs.TabPane>
      
      <Tabs.TabPane tab="M 币记录" key="3">
        {
          mcoinRecord?.length 
            ? <Table
                dataSource={mcoinRecord}
                rowKey="publicId"
                scroll={{ x: isMobile ? 300 : undefined }}
                pagination={{ 
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalRecords,
                  size: isMobile ? 'small' : 'default',
                  simple: isMobile
                }}
                columns={tableColumns}
                size={isMobile ? 'small' : 'middle'}
                onChange={handleTableChange}
              />
            : <Empty description="暂无记录" />
        }
      </Tabs.TabPane>
    </Tabs>
    <Modal 
      title="微信支付" 
      open={showPaymentModal}
      onCancel={handleDismissModal}
      footer={null}
      centered
      width={isMobile ? 300 : 400}
      maskClosable={false}
    >
      <div className={styles.qrcode}>
        <div className={styles.qrcodeWrapper}>
          <img src={qrcode} alt="支付二维码" />
        </div>
      </div>
      <div className={styles.tip}>--请使用微信扫码支付--</div>
    </Modal>

    {/* 微信绑定弹窗 */}
    <WechatBindDialog
      open={showBindModal}
      onCancel={handleCloseBindModal}
      onBindSuccess={handleBindSuccess}
    />
  </div>;
};

export default Member;
